import { getAuthHeaders } from "./authHeaders";

export interface BulkVerifyResultItem {
  id: number;
  status: "completed" | "failed";
  decision?: string;
  findings?: string;
  error?: string;
}

export interface BulkProgressState {
  open: boolean;
  total: number;
  processed: number;
  completed: number;
  failed: number;
  percent: number;
  status: "starting" | "running" | "completed" | "failed";
  message: string;
  error?: string;
  results?: BulkVerifyResultItem[];
}

export async function executeBulkAiVerification(
  ids: number[],
  apiUrl: string,
  onProgress: (state: BulkProgressState) => void,
  onComplete?: () => void,
): Promise<void> {
  const state: BulkProgressState = {
    open: true,
    total: ids.length,
    processed: 0,
    completed: 0,
    failed: 0,
    percent: 0,
    status: "starting",
    message: `Initiating AI verification for ${ids.length} process(es)...`,
    results: [],
  };
  onProgress({ ...state });

  try {
    const res = await fetch(`${apiUrl}/purchasepaybacks/bulk-verify`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ ids }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.message || err?.error?.message || `Server returned ${res.status}`);
    }

    const { batchId } = await res.json();
    state.status = "running";
    state.message = `Processing ${ids.length} process(es) in small batches via queue...`;
    onProgress({ ...state });

    const pollInterval = 1500;
    const poll = async (): Promise<void> => {
      try {
        const statusRes = await fetch(`${apiUrl}/purchasepaybacks/bulk-verify/${batchId}/status`, {
          method: "GET",
          headers: getAuthHeaders(),
        });

        if (statusRes.ok) {
          const data = await statusRes.json();
          state.completed = data.completed ?? 0;
          state.failed = data.failed ?? 0;
          state.processed = data.processed ?? (state.completed + state.failed);
          state.total = data.total ?? ids.length;
          state.percent = Math.min(100, Math.floor((state.processed / state.total) * 100));
          state.message = `Processed ${state.processed} of ${state.total} (${state.percent}%)`;
          if (Array.isArray(data.results)) {
            state.results = data.results;
          }
          onProgress({ ...state });

          if (data.status === "completed" || state.processed >= state.total) {
            state.status = "completed";
            state.percent = 100;
            state.message = `AI verification completed (${state.completed} succeeded, ${state.failed} failed)`;
            onProgress({ ...state });
            if (onComplete) onComplete();
            return;
          }
        }
      } catch (err: any) {
        console.warn("Error polling bulk verification status:", err?.message);
      }
      setTimeout(poll, pollInterval);
    };

    setTimeout(poll, 1000);
  } catch (e: any) {
    const errorMsg: string = (e && typeof e.message === "string" && e.message) ? e.message : "Failed to start bulk AI verification";
    state.status = "failed";
    state.error = errorMsg;
    state.message = errorMsg;
    onProgress({ ...state });
  }
}
